import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { SetupBanner } from "../../components/setup/SetupBanner";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { ErrorState, LoadingState } from "../../components/ui/State";
import { StatTile } from "../../components/ui/StatTile";
import { api } from "../../lib/api";
import { dashboardApi } from "../../lib/api/dashboard";
import { networkViolationsApi } from "../../lib/api/network/violations";
import { networkAccessRequestsApi } from "../../lib/api/network/accessRequests";
import { useAuth } from "../../lib/auth";
import { auditActionLabel, auditActorTypeLabel } from "../../lib/auditLabels";
import { formatDateTime, localeToBcp47 } from "../../lib/format";
import { getLocale, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import "../../components/layout/layout.css";

function todayEyebrow(): string {
  const now = new Date();
  const date = now.toLocaleDateString(localeToBcp47(getLocale()), {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return `${t("dashboard.today")} · ${date}`.toUpperCase();
}

function greetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "dashboard.goodMorning";
  if (hour < 18) return "dashboard.goodAfternoon";
  return "dashboard.goodEvening";
}

function isoHoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

const TWENTY_FOUR_HOURS_AGO = isoHoursAgo(24);

export function DashboardPage() {
  useLocale();
  const { user } = useAuth();

  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 30_000,
  });
  const summary = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: dashboardApi.summary,
    refetchInterval: 30_000,
  });
  const blocked24h = useQuery({
    queryKey: ["dashboard", "blocked-24h"],
    queryFn: () =>
      networkViolationsApi.list({
        from: TWENTY_FOUR_HOURS_AGO,
        limit: 1,
      }),
    refetchInterval: 60_000,
  });
  const totalRequests24h = useQuery({
    queryKey: ["dashboard", "all-24h"],
    queryFn: () =>
      networkViolationsApi.list({ from: TWENTY_FOUR_HOURS_AGO, limit: 1 }),
    refetchInterval: 60_000,
  });
  const openAccessReqs = useQuery({
    queryKey: ["dashboard", "open-access"],
    queryFn: () =>
      networkAccessRequestsApi.list({ status: "pending", limit: 1 }),
    refetchInterval: 60_000,
  });

  const displayName = user?.full_name || user?.email?.split("@")[0] || "";
  const greeting = displayName
    ? `${t(greetingKey())}, ${displayName}.`
    : t(greetingKey());

  const devicesTotal = summary.data?.devices.total ?? 0;
  const devicesActive = summary.data?.devices.active ?? 0;
  const devicesOnline = summary.data?.devices.online ?? devicesActive;
  const devicesStale = summary.data?.devices.stale ?? 0;
  const compiledPolicies = summary.data?.policies.compiled_total ?? 0;
  const summaryNet = summary.data?.network;
  const enforcementPct =
    summary.data?.policies.enforcement_pct ??
    (devicesTotal > 0 ? Math.round((devicesActive / devicesTotal) * 100) : 0);
  const blockedCount = summaryNet?.policy_denials_24h ?? blocked24h.data?.total ?? 0;
  const totalReqCount =
    summaryNet?.domain_requests_24h ?? totalRequests24h.data?.total ?? 0;
  const openAccessCount =
    summaryNet?.open_access_requests ??
    openAccessReqs.data?.pending_total ??
    openAccessReqs.data?.total ??
    0;
  const newDomains24h = summaryNet?.new_domains_24h ?? 0;

  const latestAudit = summary.data?.audit.latest ?? [];

  return (
    <div>
      <SetupBanner empty={devicesTotal === 0 && !summary.isLoading} />
      <PageHeader
        eyebrow={todayEyebrow()}
        title={greeting}
        subtitle={t("dashboard.subtitle")}
      />

      {summary.isLoading ? (
        <Card>
          <LoadingState />
        </Card>
      ) : summary.error ? (
        <Card>
          <ErrorState titleKey="states.errorTitle" message={(summary.error as Error).message} />
        </Card>
      ) : (
        <div className="stat-grid">
          <StatTile
            label={t("dashboard.stat.devicesOnline")}
            value={devicesOnline}
            unit={`/ ${devicesTotal}`}
            sub={`${devicesStale} ${t("dashboard.stat.stale")}`}
          />
          <StatTile
            label={t("dashboard.stat.enforcement")}
            value={`${enforcementPct}%`}
            sub={t("dashboard.stat.ofDevices").replace("{{count}}", String(devicesTotal))}
          />
          <StatTile
            label={t("dashboard.stat.domainRequests24h")}
            value={totalReqCount.toLocaleString()}
            sub={t("dashboard.stat.last24h")}
          />
          <StatTile
            label={t("dashboard.stat.blockedRequests24h")}
            value={blockedCount.toLocaleString()}
            sub={t("dashboard.stat.last24h")}
          />
          <StatTile
            label={t("dashboard.stat.openAccessRequests")}
            value={openAccessCount}
            sub={t("dashboard.stat.awaitingReview")}
          />
          <StatTile
            label={t("dashboard.stat.newDomains24h")}
            value={newDomains24h}
            sub={t("dashboard.stat.last24h")}
          />
          <StatTile
            label={t("dashboard.stat.policiesCompiled")}
            value={compiledPolicies}
            sub={t("dashboard.summary.compiledTotal")}
          />
        </div>
      )}

      <div className="split">
        <Card title={t("dashboard.needsAttentionTitle")} padding="none">
          <div style={{ padding: "8px 20px" }}>
            {openAccessCount > 0 && (
              <Link to="/insights/access-requests" className="inbox-row" style={{ textDecoration: "none" }}>
                <div className="inbox-row__count inbox-row__count--accent">{openAccessCount}</div>
                <div className="inbox-row__body">
                  <span className="inbox-row__title">{t("dashboard.attn.accessRequestsTitle")}</span>
                  <span className="inbox-row__sub">{t("dashboard.attn.accessRequestsBody")}</span>
                </div>
                <span className="inbox-row__cta">{t("common.review")} →</span>
              </Link>
            )}
            {devicesStale > 0 && (
              <Link to="/devices" className="inbox-row" style={{ textDecoration: "none" }}>
                <div className="inbox-row__count">{devicesStale}</div>
                <div className="inbox-row__body">
                  <span className="inbox-row__title">{t("dashboard.attn.staleDevicesTitle")}</span>
                  <span className="inbox-row__sub">{t("dashboard.attn.staleDevicesBody")}</span>
                </div>
                <span className="inbox-row__cta">{t("common.review")} →</span>
              </Link>
            )}
            {blockedCount > 0 && (
              <Link to="/insights/violations" className="inbox-row" style={{ textDecoration: "none" }}>
                <div className="inbox-row__count">{blockedCount}</div>
                <div className="inbox-row__body">
                  <span className="inbox-row__title">{t("dashboard.attn.blockedTitle")}</span>
                  <span className="inbox-row__sub">{t("dashboard.attn.blockedBody")}</span>
                </div>
                <span className="inbox-row__cta">{t("common.review")} →</span>
              </Link>
            )}
            {openAccessCount === 0 && devicesStale === 0 && blockedCount === 0 && (
              <div style={{ padding: "24px 0", color: "var(--text-muted)", fontSize: 13 }}>
                {t("dashboard.attn.allClear")}
              </div>
            )}
          </div>
        </Card>

        <Card title={t("dashboard.recentActivityTitle")} padding="none">
          {summary.isLoading && <LoadingState />}
          {latestAudit.length === 0 && !summary.isLoading && (
            <div style={{ padding: "24px 20px", color: "var(--text-muted)", fontSize: 13 }}>
              {t("dashboard.summary.latestAuditEmpty")}
            </div>
          )}
          {latestAudit.length > 0 && (
            <ul className="activity-list">
              {latestAudit.map((row) => (
                <li key={row.id}>
                  <span className="activity-list__time">{formatDateTime(row.created_at)}</span>
                  <span className="activity-list__text">{auditActionLabel(row.action)}</span>
                  <span className="badge badge--info">{auditActorTypeLabel(row.target_type || row.actor_type)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 24 }}>
        <Card title={t("dashboard.healthCardTitle")}>
          {health.isLoading && <LoadingState />}
          {health.error && (
            <ErrorState
              titleKey="dashboard.healthFetchError"
              message={(health.error as Error).message}
            />
          )}
          {health.data && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <span className={`badge ${health.data.db ? "badge--success" : "badge--danger"}`}>
                {t("dashboard.healthDb")} ·{" "}
                {health.data.db ? t("dashboard.healthOk") : t("dashboard.healthFail")}
              </span>
              <span className={`badge ${health.data.redis ? "badge--success" : "badge--danger"}`}>
                {t("dashboard.healthRedis")} ·{" "}
                {health.data.redis ? t("dashboard.healthOk") : t("dashboard.healthFail")}
              </span>
              {user?.company && (
                <span className="badge badge--info">
                  {user.company.name}
                  {user.company.domain ? ` · ${user.company.domain}` : ""}
                </span>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
